import { nanoid } from 'nanoid';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Product } from '@getaccept/lib-shared/src/product-library/types/product';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import type {
  ColumnType,
  CurrencyFormatOptions,
  ImageDisplayMode,
} from '../../types/pricing-table';
import type {
  PricingTableColumn,
  PricingTableRow,
  PricingTableRowValue,
  PricingTableSection,
  PricingTableCurrencySettings,
  Field,
} from '../../types';
import { KeysAllowedType, EPricingTableColumn, CustomCurrency } from '../../types/enums';
import { minColumnWidth, customColumnNamePrefix, nonGridColumns } from '../../constants';
import type { EditorProductProperty } from '../../types/editor-product-property';
import * as EditorHelper from '../editor.helper';
import * as PricingTableSummaryHelper from './pricing-table-summary.helper';

export function currencyFormat(
  value: string,
  currencySettings: PricingTableCurrencySettings
): string {
  if (!currencySettings || !currencySettings?.formatOptions?.currency) {
    return '';
  }
  const { locale, formatOptions } = currencySettings;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    ...formatOptions,
  })
    .formatToParts(Number(value))
    .map(({ type, value }) => {
      if (type === 'currency') {
        return getCustomCurrencyFormat(formatOptions) || value;
      }
      return value;
    })
    .reduce((string, part) => string + part);
}

export function numberFormat(
  value: string,
  currencySettings: PricingTableCurrencySettings
): string {
  const val = value || '0';
  const locale = currencySettings?.locale;
  if (!locale) {
    return val;
  }

  return new Intl.NumberFormat(locale).format(Number(val));
}

export function getCustomCurrencyFormat(formatOptions: CurrencyFormatOptions): string | null {
  return formatOptions.currency === CustomCurrency.Ore ? 'öre' : null;
}

/**
 * Creates a new pricing table section. Uses provided data or defaults, always generating new IDs.
 */
export function getNewPricingTableSection(
  partialSection?: Partial<PricingTableSection>
): PricingTableSection {
  const { flags } = useLaunchDarkly();
  const sectionId = nanoid(EditorHelper.NANO_ID_LENGTH);

  const columns = partialSection?.columns
    ? getNewPricingTableColumns(partialSection.columns)
    : [
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.Name,
          displayName: t('name'),
          enabled: true,
          width: 40,
        },
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.Description,
          displayName: t('description'),
          enabled: true,
          width: 0,
        },
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.Sku,
          displayName: t('sku'),
          enabled: true,
          width: 0,
        },
        ...(flags.value.imagesInPricingTables ? [createImageColumn()] : []),
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.Price,
          displayName: t('price'),
          enabled: true,
          width: 20,
        },
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.Units,
          displayName: t('units'),
          enabled: true,
          width: 10,
        },
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.Tax,
          displayName: t('tax'),
          enabled: false,
          width: 15,
        },
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.Discount,
          displayName: t('discount'),
          enabled: false,
          width: 0,
        },
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.NetPrice,
          displayName: t('net-price'),
          enabled: true,
          width: 0,
        },
        {
          id: nanoid(EditorHelper.NANO_ID_LENGTH),
          name: EPricingTableColumn.TotalPrice,
          displayName: t('cpq_amount'),
          enabled: true,
          width: 30,
        },
      ];

  const columnIdMapping = partialSection?.columns
    ? new Map(
        partialSection.columns
          .map((column, index) => (column.id ? [column.id, columns[index]!.id] : null))
          .filter((entry): entry is [string, string] => entry !== null)
      )
    : new Map();

  const getDefaultRowValueByName = (name: EPricingTableColumn, defaultValue: string) => ({
    columnId: columns.find(column => column.name === name)?.id || '',
    value: defaultValue,
  });

  const defaultRowValues = [
    getDefaultRowValueByName(EPricingTableColumn.Units, '1'),
    getDefaultRowValueByName(EPricingTableColumn.Name, ''),
    getDefaultRowValueByName(EPricingTableColumn.Description, ''),
    getDefaultRowValueByName(EPricingTableColumn.Sku, ''),
    getDefaultRowValueByName(EPricingTableColumn.Price, ''),
    getDefaultRowValueByName(EPricingTableColumn.NetPrice, ''),
    getDefaultRowValueByName(EPricingTableColumn.TotalPrice, ''),
    getDefaultRowValueByName(EPricingTableColumn.Tax, ''),
    getDefaultRowValueByName(EPricingTableColumn.Discount, ''),
  ];

  columns
    .filter((column: PricingTableColumn) => column.isCustom || !!column.productPropertyId)
    .forEach(({ id }) => {
      if (id) {
        defaultRowValues.push({ columnId: id, value: '' });
      }
    });

  const defaultRow = {
    id: nanoid(EditorHelper.NANO_ID_LENGTH),
    optionalProductFieldId: '',
    variableQuantityFieldId: '',
    values: defaultRowValues,
  };

  const rows = partialSection?.rows
    ? getNewPricingTableRows(partialSection.rows, columnIdMapping)
    : [defaultRow];

  const sectionSummary = getNewPricingTableSectionSummaryValues(
    partialSection?.sectionSummary || {
      discount: {
        value: '',
        enabled: true,
        flatFee: false,
        displayName: t('discount'),
      },
      tax: {
        value: '',
        enabled: true,
        flatFee: false,
        displayName: t('tax'),
      },
      price: {
        value: '',
        enabled: true,
        flatFee: false,
        displayName: t('cpq_total_amount'),
      },
    },
    columnIdMapping.size > 0 ? columnIdMapping : undefined
  );

  const newSection: PricingTableSection = {
    id: sectionId,
    displayName: partialSection?.displayName,
    columns,
    rows,
    sectionSummary,
    ...(flags.value.imagesInPricingTables
      ? { displayMode: partialSection?.displayMode || { imageDisplayMode: 'InlineWithName' } }
      : {}),
  };

  if (columnIdMapping.size > 0) {
    const sectionWithUpdatedMergeTags = updateMergeTagsInSection(newSection, columnIdMapping);
    return updateFieldReferencesInRows(sectionWithUpdatedMergeTags, columnIdMapping);
  }

  return newSection;
}

export function getNewPricingTableRow(
  columns: PricingTableColumn[],
  partialProduct?: Partial<Product>
): PricingTableRow {
  const rowId = nanoid(EditorHelper.NANO_ID_LENGTH);

  const getDefaultRowValueByName = (name: EPricingTableColumn, defaultValue: string) => ({
    columnId: columns.find(column => column.name === name)?.id || '',
    value:
      partialProduct && name in partialProduct && partialProduct[name] !== null
        ? partialProduct[name]
        : defaultValue,
  });

  let values: PricingTableRowValue[] = [
    getDefaultRowValueByName(EPricingTableColumn.Units, '1'),
    getDefaultRowValueByName(EPricingTableColumn.Name, ''),
    getDefaultRowValueByName(EPricingTableColumn.Description, ''),
    getDefaultRowValueByName(EPricingTableColumn.Sku, ''),
    getDefaultRowValueByName(EPricingTableColumn.Price, ''),
    getDefaultRowValueByName(EPricingTableColumn.NetPrice, ''),
    getDefaultRowValueByName(EPricingTableColumn.TotalPrice, ''),
    getDefaultRowValueByName(EPricingTableColumn.Tax, ''),
    getDefaultRowValueByName(EPricingTableColumn.Discount, ''),
  ];

  columns
    .filter(({ isCustom, productPropertyId }) => isCustom || !!productPropertyId)
    .forEach(({ id }) => {
      if (id) {
        values.push({ columnId: id, value: '' });
      }
    });

  (partialProduct?.productPropertyFields || [])
    .filter(field => !!field.value)
    .forEach(field => {
      const column = columns.find(column => column.productPropertyId === field.productPropertyId);

      if (!column) {
        return;
      }

      values = values.map(rowValue =>
        column.id === rowValue.columnId ? { columnId: column.id, value: field.value } : rowValue
      );
    });

  return {
    id: rowId,
    optionalProductFieldId: '',
    variableQuantityFieldId: '',
    values,
  };
}

export function getEnabledColumns(columns: PricingTableColumn[]): PricingTableColumn[] {
  return columns.filter(
    column => !nonGridColumns.includes(column.name as EPricingTableColumn) && column.enabled
  );
}

export function getVisibleColumns(
  columns: PricingTableColumn[],
  mode: ImageDisplayMode | undefined = 'InlineWithName'
): PricingTableColumn[] {
  const enabled = getEnabledColumns(columns);
  const imagesCol = columns.find(col => col.name === EPricingTableColumn.Images);
  return mode === 'SeparateColumn' ? enabled : enabled.filter(({ id }) => id !== imagesCol?.id);
}

export function applyColumnWidthAdjustments(
  section: PricingTableSection,
  mode: ImageDisplayMode | undefined = 'InlineWithName'
): PricingTableSection {
  const visibleCols = getVisibleColumns(section.columns, mode);
  const adjustedWidths = getAdjustedColumnWidths(visibleCols);

  return {
    ...section,
    columns: section.columns.map(col => ({
      ...col,
      width: adjustedWidths[col.id] || col.width,
    })),
  };
}

export function getRowPrice(row: PricingTableRow, columns: PricingTableColumn[]): number {
  const columnPriceId = columns.find(column => column.name === EPricingTableColumn.Price)?.id;
  return Number(row.values?.find(rowValue => rowValue.columnId === columnPriceId)?.value || 0);
}

export function getRowUnits(row: PricingTableRow, columns: PricingTableColumn[]): number {
  const columnUnitsId = columns.find(column => column.name === EPricingTableColumn.Units)?.id;
  return Number(row.values?.find(rowValue => rowValue.columnId === columnUnitsId)?.value || 0);
}

export function getRowDiscount(row: PricingTableRow, columns: PricingTableColumn[]): number {
  const columnDiscountId = columns.find(column => column.name === EPricingTableColumn.Discount)?.id;
  return Number(row.values?.find(rowValue => rowValue.columnId === columnDiscountId)?.value || 0);
}

export function getColumnIdFromName(columns: PricingTableColumn[], columnName: string) {
  return columns.find(column => column.name === columnName)?.id;
}

export function getColumnUnitsId(columns: PricingTableColumn[]): string {
  return columns?.find(column => column.name === EPricingTableColumn.Units)?.id || '';
}

export function getColumnTaxId(columns: PricingTableColumn[]): string {
  return columns.find(column => column.name === EPricingTableColumn.Tax)?.id || '';
}

export function getColumnTotalPriceId(columns: PricingTableColumn[]): string {
  return columns.find(column => column.name === EPricingTableColumn.TotalPrice)?.id || '';
}

export function getColumnDiscountId(columns: PricingTableColumn[]): string {
  return columns.find(column => column.name === EPricingTableColumn.Discount)?.id || '';
}

export function getColumnNetPriceId(columns: PricingTableColumn[]): string {
  return columns.find(column => column.name === EPricingTableColumn.NetPrice)?.id || '';
}

export function getIsColumnEnabled(
  columns: PricingTableColumn[],
  columnName: EPricingTableColumn
): boolean {
  return columns?.find(column => column.name === columnName)?.enabled || false;
}

export function calculateRowTaxSum(row: PricingTableRow, columns: PricingTableColumn[]): number {
  const taxEnabled = getIsColumnEnabled(columns, EPricingTableColumn.Tax);
  if (!taxEnabled) {
    return 0;
  }
  const { taxFlatFee, values, discountFlatFee } = row;
  const price = getRowPrice(row, columns);
  const units = getIsColumnEnabled(columns, EPricingTableColumn.Units)
    ? getRowUnits(row, columns)
    : 1;
  const totalPrice = price * units;
  const discount = getIsColumnEnabled(columns, EPricingTableColumn.Discount)
    ? getRowDiscount(row, columns)
    : 0;
  const priceAfterDiscount = discountFlatFee
    ? totalPrice - discount
    : totalPrice - totalPrice * (discount / 100);

  const columnTaxId = getColumnTaxId(columns);
  const rowTax = Number(values?.find(rowValue => rowValue.columnId === columnTaxId)?.value || 0);

  const taxSum = priceAfterDiscount * (rowTax / 100);

  if (taxFlatFee) {
    return rowTax;
  } else {
    return taxSum;
  }
}

export function calculateRowDiscountSum(
  row: PricingTableRow,
  columns: PricingTableColumn[]
): number {
  const discountEnabled = getIsColumnEnabled(columns, EPricingTableColumn.Discount);
  if (!discountEnabled) {
    return 0;
  }
  const { discountFlatFee, values = [] } = row;
  const rowTotalPrice = Number(calculateRowTotalPrice(row, columns)?.value || 0);
  const columnDiscountId = getColumnDiscountId(columns);
  const rowDiscount = Number(
    values.find(rowValue => rowValue.columnId === columnDiscountId)?.value
  );

  if (discountFlatFee) {
    return rowDiscount;
  } else {
    return (rowTotalPrice * rowDiscount) / 100;
  }
}

export function calculateRowTotalPrice(
  row: PricingTableRow,
  columns: PricingTableColumn[]
): PricingTableRowValue {
  const columnTotalPriceId = getColumnTotalPriceId(columns);
  const price = getRowPrice(row, columns);

  const units = getIsColumnEnabled(columns, EPricingTableColumn.Units)
    ? getRowUnits(row, columns)
    : 1;

  const value = String(price * units);

  return {
    columnId: columnTotalPriceId,
    value,
  };
}

export function calculateRowNetPrice(
  row: PricingTableRow,
  columns: PricingTableColumn[]
): PricingTableRowValue {
  const columnTotalPriceId = getColumnTotalPriceId(columns);
  const columnNetPriceId = getColumnNetPriceId(columns);
  const rowTotalPrice = calculateRowTotalPrice(row, columns);
  const rowWithTotalPriceCalculated: PricingTableRow = {
    ...row,
    values: [
      ...(row.values?.filter(rowValue => rowValue.columnId !== columnTotalPriceId) ?? []),
      rowTotalPrice,
    ],
  };
  const discount = getIsColumnEnabled(columns, EPricingTableColumn.Discount)
    ? calculateRowDiscountSum(rowWithTotalPriceCalculated, columns)
    : 0;

  const value = String(Number(rowTotalPrice.value) - discount);

  return {
    columnId: columnNetPriceId,
    value,
  };
}

export function getPriceAfterDiscount(
  discountFlatFee: boolean,
  discountValue: string,
  subtotal: string
): number {
  if (discountFlatFee) {
    return Number(subtotal) - Number(discountValue);
  }
  return Number(subtotal) - (Number(subtotal) * Number(discountValue)) / 100;
}

export function getPriceAfterTax(
  taxFlatFee: boolean,
  priceAfterDiscount: number,
  taxValue: string
): number {
  if (taxFlatFee) {
    return priceAfterDiscount + Number(taxValue);
  }
  return priceAfterDiscount + (priceAfterDiscount * Number(taxValue)) / 100;
}

export function getRowDiscountValue(row: PricingTableRow, columns: PricingTableColumn[]): number {
  const discountEnabled = getIsColumnEnabled(columns, EPricingTableColumn.Discount);
  if (!discountEnabled) {
    return 0;
  }
  const { values = [] } = row;
  const columnTaxId = getColumnTaxId(columns);
  const columnDiscountId = getColumnDiscountId(columns);
  const price = getRowPrice(row, columns);
  const units = getIsColumnEnabled(columns, EPricingTableColumn.Units)
    ? getRowUnits(row, columns)
    : 1;
  const rowTax = Number(values.find(rowValue => rowValue.columnId === columnTaxId)?.value);
  const discount = Number(values.find(rowValue => rowValue.columnId === columnDiscountId)?.value);
  const totalPrice = price * units + (price * units * rowTax) / 100;

  return (totalPrice * discount) / 100;
}

export function calculateRowsTotalValues(
  rows: PricingTableRow[],
  columns: PricingTableColumn[]
): PricingTableRow[] {
  const columnTotalPriceId = getColumnTotalPriceId(columns);
  const columnNetPriceId = getColumnNetPriceId(columns);
  return rows.map(row => {
    const rowTotalPrice: PricingTableRowValue = calculateRowTotalPrice(row, columns);

    const rowNetPrice: PricingTableRowValue = calculateRowNetPrice(row, columns);
    return {
      ...row,
      values: (row.values ?? []).map(rowValue => {
        if (rowValue.columnId === columnTotalPriceId) {
          return rowTotalPrice;
        } else if (rowValue.columnId === columnNetPriceId) {
          return rowNetPrice;
        } else {
          return rowValue;
        }
      }),
    };
  });
}

export function toggleOptionalProductRow(
  rows: PricingTableRow[],
  rowId: string,
  optionalProductFieldId: string
): PricingTableRow[] {
  return rows.map(row => {
    if (row.id === rowId) {
      return {
        ...row,
        optionalProductFieldId,
      };
    } else {
      return {
        ...row,
      };
    }
  });
}

export function toggleVariableQuantityRow(
  rows: PricingTableRow[],
  rowId: string,
  variableQuantityFieldId: string
): PricingTableRow[] {
  return rows.map(row => {
    if (row.id === rowId) {
      return {
        ...row,
        variableQuantityFieldId,
      };
    } else {
      return {
        ...row,
      };
    }
  });
}

export function disableColumn(columnId: string, section: PricingTableSection): PricingTableSection {
  const { columns } = section;
  const columnsAfterDisabling = getEnabledColumns(columns).filter(column => column.id !== columnId);
  const columnWidths = getAdjustedColumnWidths(columnsAfterDisabling);
  const updatedColumns: PricingTableColumn[] = columns.map(column => {
    if (column.id === columnId) {
      return { ...column, enabled: false };
    } else {
      return {
        ...column,
        ...(column.id !== getColumnDiscountId(columns) && {
          width: columnWidths[column.id] || column.width,
        }),
      };
    }
  });
  return {
    ...section,
    columns: updatedColumns,
  };
}

export function disableColumnAndReCalculate(
  columnId: string,
  section: PricingTableSection
): PricingTableSection {
  const { rows, sectionSummary } = section;
  const { columns: columnsAfterDisabling } = disableColumn(columnId, section);

  const updatedRows = calculateRowsTotalValues(rows, columnsAfterDisabling);
  const updatedSectionSummary = PricingTableSummaryHelper.calculateSectionSummary(
    updatedRows,
    columnsAfterDisabling,
    sectionSummary
  );
  return {
    ...section,
    rows: updatedRows,
    columns: columnsAfterDisabling,
    sectionSummary: updatedSectionSummary,
  };
}

export function getAdjustedColumnWidths(enabledColumns: PricingTableColumn[]): {
  [key: string]: number;
} {
  if (enabledColumns.length === 1) {
    return { [enabledColumns[0].id]: 100 };
  }
  let totalDiff = enabledColumns.reduce((acc, curr) => acc + (curr.width ?? 0), 0) - 100;
  return enabledColumns.reduce((acc, curr) => {
    if (totalDiff === 0) {
      acc[curr.id] = curr.width ?? 0;
      return acc;
    }
    const res = (curr.width ?? 0) - totalDiff;
    if (res < minColumnWidth) {
      acc[curr.id] = minColumnWidth;
      const diff = (curr.width ?? 0) - minColumnWidth;
      totalDiff -= diff;
      return acc;
    }
    acc[curr.id] = res;
    totalDiff = 0;
    return acc;
  }, {});
}

export function enableColumn(columnId: string, section: PricingTableSection): PricingTableSection {
  const { columns } = section;
  const enabledColumn = columns.find(column => column.id === columnId);
  if (!enabledColumn) {
    return section;
  }
  const columnsAfterEnabling = [...getEnabledColumns(columns), enabledColumn];
  const columnWidths = getAdjustedColumnWidths(columnsAfterEnabling);
  const updatedColumns: PricingTableColumn[] = columns.map(column => ({
    ...column,
    ...(column.id === columnId && { enabled: true }),
    ...(column.id !== getColumnDiscountId(columns) && {
      width: columnWidths[column.id] || column.width,
    }),
  }));
  return {
    ...section,
    columns: updatedColumns,
  };
}

export function enableColumnAndReCalculate(
  columnId: string,
  section: PricingTableSection
): PricingTableSection {
  const { rows, sectionSummary } = section;
  const { columns: columnsAfterEnabling } = enableColumn(columnId, section);

  const updatedRows = calculateRowsTotalValues(rows, columnsAfterEnabling);
  const updatedSectionSummary = PricingTableSummaryHelper.calculateSectionSummary(
    updatedRows,
    columnsAfterEnabling,
    sectionSummary
  );
  return {
    ...section,
    rows: updatedRows,
    columns: columnsAfterEnabling,
    sectionSummary: updatedSectionSummary,
  };
}

export function createCustomColumn(
  section: PricingTableSection,
  index?: number
): PricingTableSection {
  const noOfExistingCustomColumns = section.columns.filter(({ isCustom }) => isCustom).length;
  const name = `${customColumnNamePrefix}${noOfExistingCustomColumns + 1}`;

  const newCustomColumn = createColumn({
    name,
    displayName: t('cpq_custom_column'),
    enabled: true,
    isCustom: true,
  });

  return insertColumn(newCustomColumn, section, index);
}

export function createImageColumn() {
  return createColumn({
    name: EPricingTableColumn.Images,
    displayName: t('image'),
    type: 'Image' as ColumnType,
  });
}

export function createColumn(column: Partial<PricingTableColumn>): PricingTableColumn {
  return {
    id: nanoid(EditorHelper.NANO_ID_LENGTH),
    displayName: '',
    name: '',
    enabled: false,
    width: minColumnWidth,
    isCustom: false,
    unit: undefined,
    type: undefined,
    productPropertyId: undefined,
    ...(column ?? {}),
  };
}

export const createColumnFromProductProperty = (
  productProperty: EditorProductProperty,
  columnProps?: Partial<PricingTableColumn>
): PricingTableColumn =>
  createColumn({
    name: productProperty.name,
    displayName: productProperty.displayName,
    type: productProperty.type,
    productPropertyId: productProperty.id,
    unit: productProperty.type === 'Number' ? productProperty.unit : undefined,
    ...(columnProps ?? {}),
  });

export function insertColumn(
  newColumn: PricingTableColumn,
  section: PricingTableSection,
  index?: number
): PricingTableSection {
  const enabledColumns = getEnabledColumns(section.columns);
  const columnWidths = getAdjustedColumnWidths([...enabledColumns, newColumn]);
  const updatedColumns: PricingTableColumn[] = section.columns.map(column => ({
    ...column,
    width: columnWidths[column.id] || column.width,
  }));

  const enabledColumnsInsertionIndex = index ?? enabledColumns.length - 1;
  const columnIdAtIndex = enabledColumns[enabledColumnsInsertionIndex]?.id;

  const updatedColumnsInsertionIndex = updatedColumns.findIndex(({ id }) => id === columnIdAtIndex);
  updatedColumns.splice(updatedColumnsInsertionIndex, 0, {
    ...newColumn,
    width: columnWidths[newColumn.id] || newColumn.width,
  });

  const updatedRows = section.rows.map(row => ({
    ...row,
    values: [...(row.values ?? []), { columnId: newColumn.id, value: '' }],
  }));

  return {
    ...section,
    rows: updatedRows,
    columns: updatedColumns,
  };
}

export function isKeyAllowed(evt: KeyboardEvent, type?: KeysAllowedType): void {
  const keysAllowed: string[] = ['Enter', 'Esc', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  if (type === KeysAllowedType.Decimals) {
    keysAllowed.push('.', ',');
  }
  const keyPressed: string = evt.key;

  if (!keysAllowed.includes(keyPressed)) {
    evt.preventDefault();
  }
}

export function moveArrayItemUp<T>(array: T[], item: T): T[] {
  const index = array.indexOf(item);
  if (index === -1 || index === 0) {
    return array;
  }
  const newArray = [...array];
  newArray.splice(index - 1, 2, array[index], array[index - 1]);
  return newArray;
}

export function moveArrayItemDown<T>(array: T[], item: T): T[] {
  const index = array.indexOf(item);
  if (index === -1 || index === array.length - 1) {
    return array;
  }
  const newArray = [...array];
  newArray.splice(index, 1);
  newArray.splice(index + 1, 0, item);
  return newArray;
}

export function getIsRowEnabled(row: PricingTableRow, fields: Field[]) {
  return !fields.some(field => field.id === row.optionalProductFieldId && field.value === '0');
}

export function getEnabledRows(rows: PricingTableRow[], fields: Field[]): PricingTableRow[] {
  return rows.filter(row => getIsRowEnabled(row, fields));
}

export function getRowsFromInputFieldValues(
  rows: PricingTableRow[],
  columns: PricingTableColumn[],
  fields: Field[]
): PricingTableRow[] {
  return rows.map(row => getRowFromInputFieldValues(row, columns, fields));
}

export function getRowFromInputFieldValues(
  row: PricingTableRow,
  columns: PricingTableColumn[],
  fields: Field[]
): PricingTableRow {
  const field = fields.find(field => field.id === row.variableQuantityFieldId);
  const unitsEnabled = getIsColumnEnabled(columns, EPricingTableColumn.Units);
  if (field) {
    return {
      ...row,
      values: (row.values ?? []).map(rowValue => {
        if (unitsEnabled && rowValue.columnId === getColumnUnitsId(columns)) {
          return {
            ...rowValue,
            value: field.value,
          };
        } else {
          return rowValue;
        }
      }),
    };
  } else {
    return row;
  }
}

export function sectionsWithEnabledRows(
  sections: PricingTableSection[],
  fields: Field[]
): PricingTableSection[] {
  return sections.map(section => ({
    ...section,
    rows: getEnabledRows(section.rows, fields),
  }));
}

export function sectionsWithRowsFromInputValues(
  sections: PricingTableSection[],
  fields: Field[]
): PricingTableSection[] {
  return sections.map(section => ({
    ...section,
    rows: getRowsFromInputFieldValues(section.rows, section.columns, fields),
  }));
}

export function tableContainsOptProdOrVarQty(sections: PricingTableSection[]): boolean {
  return sections?.some(section =>
    section.rows.some(row => !!row.optionalProductFieldId || !!row.variableQuantityFieldId)
  );
}

export const fieldsWithRecipientInput = (fields: Field[], recipientInput: EditorRecipientInput[]) =>
  fields?.map(field => ({
    ...field,
    value: recipientInput?.find(input => input.fieldId === field.id)?.value || field.value,
  })) || [];

export const getGridStyle = (columnWidths: number[]) => {
  if (!columnWidths || columnWidths.length === 0) {
    return '';
  }

  return `grid-template-columns: ${columnWidths?.join('% ')}%;`;
};

export const getNewPricingTableColumns = (
  originalColumns: PricingTableColumn[]
): PricingTableColumn[] =>
  originalColumns.map(column => ({
    ...column,
    id: nanoid(EditorHelper.NANO_ID_LENGTH),
  }));

export const getNewPricingTableRows = (
  originalRows: PricingTableRow[],
  columnIdMapping: Map<string, string>
): PricingTableRow[] =>
  originalRows.map(row => {
    const newRowId = nanoid(EditorHelper.NANO_ID_LENGTH);

    const newValues = (row.values || []).map(value => ({
      ...value,
      columnId: columnIdMapping.get(value.columnId) || value.columnId,
    }));

    return {
      ...row,
      id: newRowId,
      values: newValues,
    };
  });

export const getNewPricingTableSectionSummaryValues = (
  originalSectionSummary: PricingTableSection['sectionSummary'],
  columnIdMapping?: Map<string, string>
): PricingTableSection['sectionSummary'] => {
  let { extendedSummaries } = originalSectionSummary;
  if (extendedSummaries && columnIdMapping) {
    extendedSummaries = extendedSummaries.map(summary =>
      summary.columnId && columnIdMapping.has(summary.columnId)
        ? { ...summary, columnId: columnIdMapping.get(summary.columnId)! }
        : summary
    );
  }
  return {
    displayName: originalSectionSummary.displayName,
    discount: {
      ...originalSectionSummary.discount,
      value: '',
    },
    tax: {
      ...originalSectionSummary.tax,
      value: '',
    },
    price: {
      ...originalSectionSummary.price,
      value: '',
    },
    extendedSummaries,
  };
};

/**
 * Updates fields array with new fields from duplicated section
 */
export function updateFieldsWithDuplicatedFields(
  originalFields: Field[],
  section: PricingTableSection
): Field[] {
  const referencedFieldIds = collectReferencedFieldIds(section);
  const idMapping = new Map(
    referencedFieldIds.map(fieldId => [fieldId, nanoid(EditorHelper.NANO_ID_LENGTH)])
  );

  return referencedFieldIds
    .map(fieldId => {
      const field = originalFields.find(f => f.id === fieldId);
      if (field) {
        return { ...field, id: idMapping.get(fieldId) };
      }
      return undefined;
    })
    .filter(Boolean) as Field[];
}

/**
 * Creates a deep copy of a pricing section with new IDs for all elements,
 * recreated merge tags, and duplicated fields
 */
export function duplicatePricingTableSection(
  section: PricingTableSection,
  fields: Field[]
): { section: PricingTableSection; newFields: Field[] } {
  const { id, ...rest } = section;
  const newSection = getNewPricingTableSection({ ...rest });

  const referencedFieldIds = collectReferencedFieldIds(section);

  const idMapping = new Map(
    referencedFieldIds.map(fieldId => [fieldId, nanoid(EditorHelper.NANO_ID_LENGTH)])
  );

  const sectionWithUpdatedMergeTags = updateMergeTagsInSection(newSection, idMapping);
  const sectionWithUpdatedFieldReferences = updateFieldReferencesInRows(
    sectionWithUpdatedMergeTags,
    idMapping
  );

  const newFields = referencedFieldIds
    .map(fieldId => {
      const field = fields.find(f => f.id === fieldId);
      if (field) {
        return { ...field, id: idMapping.get(fieldId) };
      }
      return undefined;
    })
    .filter(Boolean) as Field[];

  return { section: sectionWithUpdatedFieldReferences, newFields };
}

const collectReferencedFieldIds = (section: PricingTableSection): string[] => {
  const rowFieldIds = section.rows.flatMap(row =>
    [row.optionalProductFieldId, row.variableQuantityFieldId].filter((id): id is string =>
      Boolean(id)
    )
  );

  const prosemirrorFieldIds = section.rows.flatMap(row =>
    (row.values ?? [])
      .filter(value => typeof value.value === 'string')
      .flatMap(value => {
        try {
          const parsed = JSON.parse(value.value!);
          if (parsed && typeof parsed === 'object') {
            return getTagIdsFromProsemirrorContent(value.value!);
          }
        } catch {
          // Not a JSON string, skip
        }
        return [];
      })
  );

  const allFieldIds = [...rowFieldIds, ...prosemirrorFieldIds];
  const seen = new Set<string>();

  return allFieldIds.filter(fieldId => {
    if (seen.has(fieldId)) {
      return false;
    }
    seen.add(fieldId);
    return true;
  });
};

const getTagIdsFromProsemirrorContent = (content: string): string[] => {
  if (!content) {
    return [];
  }
  return content
    .split('"tagId":"')
    .slice(1)
    .filter(tagId => !tagId.startsWith('"'))
    .map(id => id.substring(0, EditorHelper.NANO_ID_LENGTH));
};

const updateMergeTagsInSection = (
  section: PricingTableSection,
  idMapping: Map<string, string>
): PricingTableSection => {
  const updateMergeTags = (text: string | undefined): string => {
    if (!text) return '';

    let updatedContent = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object') {
        updatedContent = Array.from(idMapping.entries()).reduce(
          (content, [oldId, newId]) =>
            content.replace(new RegExp(`"tagId":"${oldId}"`, 'g'), `"tagId":"${newId}"`),
          updatedContent
        );
      }
    } catch {
      // Not a JSON string, continue to plain text replacement
    }

    return Array.from(idMapping.entries()).reduce(
      (content, [oldId, newId]) => content.replace(new RegExp(`{{${oldId}}}`, 'g'), `{{${newId}}}`),
      updatedContent
    );
  };

  const updatedRows = section.rows.map(row => ({
    ...row,
    values: (row.values ?? []).map(value => ({
      ...value,
      value: typeof value.value === 'string' ? updateMergeTags(value.value) : value.value,
    })),
  }));

  const updatedSectionSummary = section.sectionSummary
    ? {
        ...section.sectionSummary,
        price: section.sectionSummary.price
          ? {
              ...section.sectionSummary.price,
              value: updateMergeTags(section.sectionSummary.price.value),
            }
          : section.sectionSummary.price,
        discount: section.sectionSummary.discount
          ? {
              ...section.sectionSummary.discount,
              value: updateMergeTags(section.sectionSummary.discount.value),
            }
          : section.sectionSummary.discount,
        tax: section.sectionSummary.tax
          ? {
              ...section.sectionSummary.tax,
              value: updateMergeTags(section.sectionSummary.tax.value),
            }
          : section.sectionSummary.tax,
      }
    : section.sectionSummary;

  return {
    ...section,
    rows: updatedRows,
    sectionSummary: updatedSectionSummary,
  };
};

const updateFieldReferencesInRows = (
  section: PricingTableSection,
  idMapping: Map<string, string>
): PricingTableSection => {
  const updatedRows = section.rows.map(row => ({
    ...row,
    optionalProductFieldId: row.optionalProductFieldId
      ? idMapping.get(row.optionalProductFieldId) || row.optionalProductFieldId
      : row.optionalProductFieldId,
    variableQuantityFieldId: row.variableQuantityFieldId
      ? idMapping.get(row.variableQuantityFieldId) || row.variableQuantityFieldId
      : row.variableQuantityFieldId,
  }));

  return {
    ...section,
    rows: updatedRows,
  };
};
